/* Pretty Panther — header wishlist (count badge + drawer). Saved items live in localStorage "pp:wishlist" (product ids, written by pp-theme.js). */
(() => {
  const WISH_KEY = 'pp:wishlist';
  const HANDLE_KEY = 'pp:wishlist-handles';
  const get = (key, fallback) => {
    try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
  };
  const set = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
  };

  const updateCount = () => {
    const n = get(WISH_KEY, []).length;
    document.querySelectorAll('[data-pp-wish-count]').forEach((el) => {
      el.textContent = n > 99 ? '99+' : String(n);
      el.hidden = n === 0;
    });
    document.querySelectorAll('.pp-wish-trigger').forEach((btn) => {
      btn.setAttribute('aria-label', n ? `Wishlist, ${n} saved` : 'Wishlist');
    });
  };

  // Remember each saved product's handle so the drawer can load its tile.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-pp-wishlist]');
    if (!btn) return;
    const id = btn.dataset.ppWishlist;
    const holder = btn.closest('[data-handle]');
    const handle = btn.dataset.handle || holder?.dataset.handle || btn.closest('[data-pp-product]')?.dataset.handle;
    if (id && handle) {
      const map = get(HANDLE_KEY, {});
      map[id] = handle;
      set(HANDLE_KEY, map);
    }
    // pp-theme.js updates the list in its own click handler; read it afterwards.
    setTimeout(() => {
      updateCount();
      const drawerItem = btn.closest('.pp-wish__item');
      if (drawerItem && !get(WISH_KEY, []).includes(id)) {
        drawerItem.remove();
        if (!document.querySelector('.pp-wish__item')) renderDrawer();
      }
    }, 0);
  });

  let rendering = false;
  async function renderDrawer() {
    const body = document.querySelector('[data-pp-wish-body]');
    if (!body || rendering) return;
    rendering = true;
    const ids = get(WISH_KEY, []);
    const map = get(HANDLE_KEY, {});
    const handles = ids.map((id) => map[id]).filter(Boolean);

    if (!handles.length) {
      body.innerHTML = `
        <div class="pp-wish__empty">
          <p>Your wishlist is empty.</p>
          <p class="pp-muted">Tap the heart on any product to save it here.</p>
          <a class="pp-btn" href="/collections/bestsellers">Shop bestsellers</a>
        </div>`;
      rendering = false;
      return;
    }

    body.innerHTML = '<p class="pp-muted">Loading your wishlist…</p>';
    const tiles = await Promise.all(
      handles.map(async (handle) => {
        try {
          const res = await fetch(`/products/${encodeURIComponent(handle)}?view=pp-tile`);
          return res.ok ? (await res.text()).trim() : '';
        } catch { return ''; }
      })
    );
    const list = document.createElement('ul');
    list.className = 'pp-wish__grid';
    list.setAttribute('role', 'list');
    tiles.filter(Boolean).forEach((html) => {
      const li = document.createElement('li');
      li.className = 'pp-wish__item';
      li.innerHTML = html;
      list.appendChild(li);
    });
    body.replaceChildren(list);
    // Let pp-theme.js wire up the hearts and quick-add forms in the new tiles.
    list.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
    rendering = false;
  }

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-pp-open="pp-wish-drawer"]')) renderDrawer();
  });
  window.addEventListener('storage', (e) => { if (e.key === WISH_KEY) updateCount(); });

  // Place the wishlist button (markup from layout/theme.liquid) to the left of the account icon.
  const placeWishButton = () => {
    const tpl = document.getElementById('pp-wish-button');
    if (!tpl) return;
    document.querySelectorAll('header-actions').forEach((actions) => {
      if (actions.querySelector('.pp-wish-trigger')) return;
      const btn = tpl.content.firstElementChild.cloneNode(true);
      const anchor = actions.querySelector('.account-button') || actions.firstElementChild;
      actions.insertBefore(btn, anchor);
    });
  };

  const init = () => { placeWishButton(); updateCount(); };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', () => { placeWishButton(); updateCount(); });
})();

/* Pretty Panther — header search drop-down.
   Click in the bar: categories and brands. Type 2+ letters: live product, brand/collection and suggested-search results
   from Shopify's predictive search (/search/suggest.json). */
(() => {
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
  const locale = document.documentElement.lang || 'en';
  // Match the shop's money format (€1.234,50) so search prices look like the rest of the site.
  const money = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    if (currency === 'EUR') {
      const [whole, cents] = n.toFixed(2).split('.');
      return `€${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${cents}`;
    }
    try { return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(n); } catch { return `${n.toFixed(2)} ${currency}`; }
  };
  const sized = (url, w) => {
    if (!url) return '';
    try { const u = new URL(url, location.origin); u.searchParams.set('width', w); return u.toString(); } catch { return url; }
  };

  function setup(form) {
    if (form.dataset.ppReady) return;
    form.dataset.ppReady = '1';
    const input = form.querySelector('[data-pp-search-input]');
    const drop = form.querySelector('[data-pp-search-drop]');
    const start = form.querySelector('[data-pp-search-start]');
    const results = form.querySelector('[data-pp-search-results]');
    if (!input || !drop) return;

    let timer = null;
    let controller = null;
    let lastQuery = null;

    const open = () => {
      drop.hidden = false;
      form.classList.add('is-open');
      input.setAttribute('aria-expanded', 'true');
    };
    const close = () => {
      drop.hidden = true;
      form.classList.remove('is-open');
      input.setAttribute('aria-expanded', 'false');
    };
    const showStart = () => { start.hidden = false; results.hidden = true; };

    async function search(q) {
      if (q === lastQuery) return;
      lastQuery = q;
      controller?.abort();
      controller = new AbortController();
      const params = new URLSearchParams({
        q,
        'resources[type]': 'product,collection,query',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
      });
      let data;
      try {
        const res = await fetch(`${root}search/suggest.json?${params}`, { signal: controller.signal });
        if (!res.ok) throw new Error(res.status);
        data = (await res.json()).resources?.results || {};
      } catch (err) {
        if (err.name === 'AbortError') return;
        data = {};
      }
      const products = data.products || [];
      const collections = data.collections || [];
      const queries = (data.queries || []).filter((s) => s.text && s.text.toLowerCase() !== q.toLowerCase());

      let html = '';
      if (queries.length || collections.length) {
        html += '<ul class="pp-search-drop__chips" role="list">';
        queries.slice(0, 4).forEach((s) => { html += `<li><a href="${esc(s.url)}">${esc(s.text)}</a></li>`; });
        collections.slice(0, 4).forEach((c) => { html += `<li><a href="${esc(c.url)}">${esc(c.title)}</a></li>`; });
        html += '</ul>';
      }
      if (products.length) {
        html += '<p class="pp-search-drop__label">Products</p><ul class="pp-search-drop__products" role="list">';
        products.forEach((p) => {
          const img = p.featured_image?.url || p.image;
          const from = p.price_max && p.price_min && p.price_max !== p.price_min ? 'From ' : '';
          html += `<li><a class="pp-search-drop__product" href="${esc(p.url)}">
            ${img ? `<img src="${esc(sized(img, 120))}" alt="" loading="lazy" width="56" height="70">` : '<span></span>'}
            <span><span class="pp-search-drop__vendor">${esc(p.vendor)}</span>
            <span class="pp-search-drop__title">${esc(p.title)}</span>
            <span class="pp-search-drop__price">${from}${esc(money(p.price_min ?? p.price))}</span></span></a></li>`;
        });
        html += '</ul>';
      } else {
        html += `<p class="pp-search-drop__empty">No products match “${esc(q)}”. Try a brand, a scent or a product type.</p>`;
      }
      html += `<a class="pp-search-drop__all" href="${root}search?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last">See all results for “${esc(q)}” →</a>`;
      results.innerHTML = html;
      start.hidden = true;
      results.hidden = false;
    }

    input.addEventListener('focus', () => {
      if (input.value.trim().length >= 2) search(input.value.trim()); else showStart();
      open();
    });
    input.addEventListener('click', open);
    input.addEventListener('input', () => {
      const q = input.value.trim();
      clearTimeout(timer);
      open();
      if (q.length < 2) { lastQuery = null; showStart(); return; }
      timer = setTimeout(() => search(q), 200);
    });
    form.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { close(); input.focus(); return; }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const links = [...drop.querySelectorAll('a')].filter((a) => a.offsetParent !== null);
      if (!links.length) return;
      e.preventDefault();
      const i = links.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown' ? (i + 1) % links.length : i <= 0 ? -1 : i - 1;
      if (next === -1) input.focus(); else links[next].focus();
    });
    document.addEventListener('pointerdown', (e) => { if (!form.contains(e.target)) close(); });
    form.addEventListener('focusout', (e) => { if (e.relatedTarget && !form.contains(e.relatedTarget)) close(); });
  }

  const init = () => document.querySelectorAll('[data-pp-search]').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
